import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  AccessTime,
  BugReport,
  TrendingUp,
  Info
} from '@mui/icons-material';
import { authGet } from '../../services/apiService';

/**
 * AverageDaysOpenInfo component for displaying average days issues have been open
 * @returns {JSX.Element} AverageDaysOpenInfo component
 */
const AverageDaysOpenInfo = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch average days open data on component mount
  useEffect(() => {
    const fetchAverageDaysOpen = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to get average days open
        const response = await authGet('/api/workitems/production-bugs/average-days-open');
        
        if (response.success) {
          setData(response.data);
          setError(null);
          console.log('Fetched average days open data:', response.data);
        } else {
          throw new Error(response.message || 'Failed to load average days open data');
        }
      } catch (err) {
        console.error('Error fetching average days open data:', err);
        setError('Failed to load average days open data. Please try again later.');
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAverageDaysOpen();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No data available for average days open.
      </Alert>
    );
  }

  const getDaysOpenColor = (days) => {
    if (days <= 7) return 'success';
    if (days <= 30) return 'warning';
    return 'error';
  };

  const getDaysOpenLabel = (days) => {
    if (days <= 7) return 'Good';
    if (days <= 30) return 'Moderate';
    return 'Needs Attention';
  };

  return (
    <Box>
      {/* Main Info Card */}
      <Card sx={{ mb: 3, background: 'white', color: '#ff9800', border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <AccessTime sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h5" component="h2">
              Average Days Open
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {data.averageDaysOpenBlocked || 0}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Days (Blocked/Clarification)
                </Typography>
                <Chip 
                  label={getDaysOpenLabel(data.averageDaysOpenBlocked || 0)}
                  color={getDaysOpenColor(data.averageDaysOpenBlocked || 0)}
                  sx={{ mt: 1, fontWeight: 'bold' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {data.averageDaysOpenNonBlocked}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Days (Active Issues)
                </Typography>
                <Chip 
                  label={getDaysOpenLabel(data.averageDaysOpenNonBlocked)}
                  color={getDaysOpenColor(data.averageDaysOpenNonBlocked)}
                  sx={{ mt: 1, fontWeight: 'bold' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {data.totalIssues}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Total Issues
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {data.blockedIssues} blocked/clarification, {data.nonBlockedIssues} active
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                  Status
                </Typography>
                <Chip 
                  label={data.averageDaysOpen <= 14 ? 'Healthy' : 'Review Needed'}
                  color={data.averageDaysOpen <= 14 ? 'success' : 'warning'}
                  sx={{ fontWeight: 'bold' }}
                />
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  {data.blockedIssues > 0 ? `${data.blockedIssues} blocked/clarification issues` : 'No blocked/clarification issues'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top 10 Oldest Blocked Issues */}
      {data.breakdownBlocked && data.breakdownBlocked.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BugReport sx={{ mr: 1, color: '#f44336' }} />
              <Typography variant="h6" component="h3">
                Top 10 Oldest Blocked/Clarification Issues
              </Typography>
              <Tooltip title="Blocked and clarification required issues that have been open the longest and may need attention">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Priority</strong></TableCell>
                    <TableCell align="right"><strong>Days Open</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.breakdownBlocked.map((issue, index) => (
                    <TableRow key={issue.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {issue.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 300, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {issue.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={issue.state} 
                          size="small" 
                          color={issue.state === 'Active' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={issue.priority || 'N/A'} 
                          size="small" 
                          color={issue.priority === 'P1' ? 'error' : issue.priority === 'P2' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: getDaysOpenColor(issue.daysOpen) === 'error' ? '#f44336' : 
                                     getDaysOpenColor(issue.daysOpen) === 'warning' ? '#ff9800' : '#4caf50'
                            }}
                          >
                            {issue.daysOpen}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>
                            days
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Top 10 Oldest Non-Blocked Issues */}
      {data.breakdownNonBlocked && data.breakdownNonBlocked.length > 0 && (
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BugReport sx={{ mr: 1, color: '#4caf50' }} />
              <Typography variant="h6" component="h3">
                Top 10 Oldest Active Issues
              </Typography>
              <Tooltip title="Active issues (excluding blocked and clarification required) that have been open the longest">
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Priority</strong></TableCell>
                    <TableCell align="right"><strong>Days Open</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.breakdownNonBlocked.map((issue, index) => (
                    <TableRow key={issue.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {issue.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          maxWidth: 300, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {issue.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={issue.state} 
                          size="small" 
                          color={issue.state === 'Active' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={issue.priority || 'N/A'} 
                          size="small" 
                          color={issue.priority === 'P1' ? 'error' : issue.priority === 'P2' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: getDaysOpenColor(issue.daysOpen) === 'error' ? '#f44336' : 
                                     getDaysOpenColor(issue.daysOpen) === 'warning' ? '#ff9800' : '#4caf50'
                            }}
                          >
                            {issue.daysOpen}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>
                            days
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AverageDaysOpenInfo;
